// Production-Ready Quiz Viewer
// Features: Edit/Preview modes, explanations, timer, retry logic, keyboard navigation, analytics

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Trophy,
  Clock,
  AlertCircle,
  Lightbulb,
  BarChart3,
  BookOpen,
  Download,
  Keyboard,
  FileText,
  FileImage,
  Pencil,
  Eye,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { contentStudioClient } from '@/lib/content-studio/client';
import { contentStudioToBuilder, builderToContentStudio } from '@/lib/content-studio/quiz-converter';
import type { Quiz, QuizQuestion, UserAnswer, QuizGradingResult } from '@/lib/content-studio/types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useContentExporter } from '@/hooks/useContentExporter';
import { QuizBuilder, type QuizConfig as BuilderQuizConfig } from '@/components/pathways/QuizBuilder';

interface QuizViewerProps {
  quiz: Quiz;
  onQuizUpdate?: (updatedQuiz: Quiz) => void;
  className?: string;
}

interface QuestionResult {
  questionId: string;
  isCorrect: boolean;
  explanation?: string;
}

export function QuizViewer({ quiz, onQuizUpdate, className = '' }: QuizViewerProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [editableQuiz, setEditableQuiz] = useState<Quiz>(quiz);
  const [builderConfig, setBuilderConfig] = useState<BuilderQuizConfig>(() => contentStudioToBuilder(quiz));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [gradingResult, setGradingResult] = useState<QuizGradingResult | null>(null);
  const [showExplanation, setShowExplanation] = useState<string | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Export functionality
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();
  
  // Timer state
  const [startTime] = useState<Date>(new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false);

  const currentQuestion = editableQuiz.questions[currentIndex];
  const totalQuestions = editableQuiz.questions.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  // Timer effect
  useEffect(() => {
    if (isSubmitted || timerPaused) return;
    
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((new Date().getTime() - startTime.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, isSubmitted, timerPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentIndex < totalQuestions - 1) {
            goToNext();
          }
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          e.preventDefault();
          const optionIndex = parseInt(e.key) - 1;
          if ((currentQuestion.type === 'multiple-choice' || currentQuestion.type === 'scenario') && currentQuestion.options?.[optionIndex]) {
            handleAnswer(currentQuestion.id, String(optionIndex));
          }
          break;
        case 't':
        case 'f':
          e.preventDefault();
          if (currentQuestion.type === 'true-false') {
            handleAnswer(currentQuestion.id, e.key === 't' ? 'true' : 'false');
          }
          break;
        case '?':
          setShowShortcuts(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, currentQuestion, totalQuestions]);

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  // Helper: get display text for an answer (converts index to option text)
  const getAnswerDisplayText = (question: QuizQuestion, answer: string | number | boolean): string => {
    if (question.type === 'multiple-choice' || question.type === 'scenario') {
      const idx = Number(answer);
      if (!isNaN(idx) && question.options?.[idx]) {
        return question.options[idx];
      }
      return String(answer);
    }
    if (question.type === 'true-false') {
      const val = typeof answer === 'boolean' ? answer : answer === 'true';
      return val ? 'True' : 'False';
    }
    return String(answer);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(totalQuestions - 1, prev + 1));
  };

  const handleSubmit = async () => {
    if (answeredCount < totalQuestions) {
      toast.warning(`Please answer all questions (${answeredCount}/${totalQuestions} answered)`);
      return;
    }

    setIsGrading(true);
    setTimerPaused(true);

    try {
      const userAnswers: UserAnswer[] = editableQuiz.questions.map((q) => ({
        questionId: q.id,
        answer: answers[q.id] || '',
      }));

      const result = await contentStudioClient.gradeQuiz(editableQuiz.questions, userAnswers);
      setGradingResult(result);
      setIsSubmitted(true);
      
      if (result.passed) {
        toast.success('Congratulations! You passed the quiz!');
      } else {
        toast.info('Quiz complete. Review your answers below.');
      }
    } catch (error) {
      console.error('Failed to grade quiz:', error);
      toast.error('Failed to grade quiz');
    } finally {
      setIsGrading(false);
    }
  };

  const handleRetry = (mode: 'all' | 'incorrect') => {
    if (mode === 'incorrect' && gradingResult) {
      // Only clear answers for incorrect questions
      const incorrectIds = gradingResult.results
        ?.filter(r => !r.isCorrect)
        .map(r => r.questionId) || [];
      
      const newAnswers = { ...answers };
      incorrectIds.forEach(id => delete newAnswers[id]);
      setAnswers(newAnswers);
      
      // Navigate to first incorrect question
      const firstIncorrectIndex = editableQuiz.questions.findIndex(q => incorrectIds.includes(q.id));
      setCurrentIndex(firstIncorrectIndex >= 0 ? firstIncorrectIndex : 0);
    } else {
      setAnswers({});
      setCurrentIndex(0);
    }
    
    setIsSubmitted(false);
    setGradingResult(null);
    setShowExplanation(null);
    setAttemptNumber(prev => prev + 1);
    setTimerPaused(false);
  };

  const getQuestionResult = (questionId: string): QuestionResult | null => {
    if (!gradingResult) return null;
    return gradingResult.results?.find((r) => r.questionId === questionId) || null;
  };

  // Export results
  const exportResults = () => {
    if (!gradingResult) return;

    const exportData = {
      quiz: editableQuiz.title,
      attemptNumber,
      completedAt: new Date().toISOString(),
      timeSpent: formatTime(elapsedSeconds),
      score: gradingResult.percentage,
      passed: gradingResult.passed,
      results: editableQuiz.questions.map(q => ({
        question: q.question,
        userAnswer: answers[q.id],
        correctAnswer: q.correctAnswer,
        isCorrect: getQuestionResult(q.id)?.isCorrect,
      })),
      areasToImprove: gradingResult.areasToImprove,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${editableQuiz.title.replace(/\s+/g, '-').toLowerCase()}-results.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Results exported!');
  };

  // Calculate analytics
  const analytics = useMemo(() => {
    if (!gradingResult) return null;

    const byDifficulty = {
      easy: { total: 0, correct: 0 },
      medium: { total: 0, correct: 0 },
      hard: { total: 0, correct: 0 },
    };

    const byType = {
      'multiple-choice': { total: 0, correct: 0 },
      'true-false': { total: 0, correct: 0 },
      'short-answer': { total: 0, correct: 0 },
      'scenario': { total: 0, correct: 0 },
    };

    editableQuiz.questions.forEach(q => {
      const result = getQuestionResult(q.id);
      const difficulty = q.difficulty || 'medium';
      
      byDifficulty[difficulty].total++;
      byType[q.type].total++;
      
      if (result?.isCorrect) {
        byDifficulty[difficulty].correct++;
        byType[q.type].correct++;
      }
    });

    return { byDifficulty, byType };
  }, [gradingResult, editableQuiz.questions]);

  // ─── Edit Mode ───────────────────────────────────────────────────────────
  if (mode === 'edit') {
    const handleBuilderChange = (config: BuilderQuizConfig) => {
      setBuilderConfig(config);
      setHasUnsavedChanges(true);
    };

    const handleSaveChanges = () => {
      const updatedQuiz = builderToContentStudio(builderConfig, editableQuiz);
      setEditableQuiz(updatedQuiz);
      setHasUnsavedChanges(false);
      onQuizUpdate?.(updatedQuiz);
      toast.success('Quiz updated successfully');
    };

    return (
      <div className={`space-y-4 ${className}`}>
        {/* Mode Toggle Bar */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1">
              <Pencil className="w-3 h-3" />
              Edit Mode
            </Badge>
            <span className="text-xs text-muted-foreground">
              {builderConfig.questions.length} questions
            </span>
            {hasUnsavedChanges && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                Unsaved changes
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <Button size="sm" variant="outline" onClick={handleSaveChanges} className="gap-1">
                <Save className="w-3 h-3" />
                Save
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                if (hasUnsavedChanges) {
                  handleSaveChanges();
                }
                setMode('preview');
              }}
              className="gap-1"
            >
              <Eye className="w-3 h-3" />
              Preview Quiz
            </Button>
          </div>
        </div>

        {/* QuizBuilder Editor */}
        <ScrollArea className="h-[calc(100vh-280px)]">
          <QuizBuilder value={builderConfig} onChange={handleBuilderChange} />
        </ScrollArea>
      </div>
    );
  }

  // Results View
  if (isSubmitted && gradingResult) {
    return (
      <TooltipProvider>
        <ScrollArea className={`h-full ${className}`}>
          <div className="space-y-4 p-1">
            {/* Score Card */}
            <Card className={cn(
              "bg-gradient-to-br",
              gradingResult.passed 
                ? "from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800"
                : "from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800"
            )}>
              <CardContent className="pt-6 text-center">
                <Trophy className={cn(
                  "w-16 h-16 mx-auto mb-4",
                  gradingResult.passed ? "text-green-600" : "text-amber-600"
                )} />
                <h2 className="text-2xl font-bold mb-2">Quiz Complete!</h2>
                <div className={cn(
                  "text-5xl font-bold mb-2",
                  gradingResult.passed ? "text-green-600" : "text-amber-600"
                )}>
                  {gradingResult.percentage}%
                </div>
                <p className="text-muted-foreground mb-4">
                  {gradingResult.correctAnswers} of {gradingResult.totalQuestions} correct
                </p>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatTime(elapsedSeconds)}
                  </span>
                  <span>Attempt #{attemptNumber}</span>
                </div>
                <Badge 
                  variant={gradingResult.passed ? 'default' : 'secondary'}
                  className={gradingResult.passed ? 'bg-green-600' : ''}
                >
                  {gradingResult.passed ? '✓ Passed!' : 'Keep Practicing'}
                </Badge>
                {gradingResult.feedback && (
                  <p className="mt-4 text-sm text-muted-foreground">{gradingResult.feedback}</p>
                )}
              </CardContent>
            </Card>

            {/* Analytics */}
            {analytics && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Performance Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">By Difficulty</p>
                      {Object.entries(analytics.byDifficulty).map(([level, data]) => (
                        data.total > 0 && (
                          <div key={level} className="flex items-center justify-between text-sm mb-1">
                            <span className="capitalize">{level}</span>
                            <span className={data.correct === data.total ? 'text-green-600' : ''}>
                              {data.correct}/{data.total}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">By Type</p>
                      {Object.entries(analytics.byType).map(([type, data]) => (
                        data.total > 0 && (
                          <div key={type} className="flex items-center justify-between text-sm mb-1">
                            <span className="capitalize">{type.replace('-', ' ')}</span>
                            <span className={data.correct === data.total ? 'text-green-600' : ''}>
                              {data.correct}/{data.total}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Areas to Improve */}
            {gradingResult.areasToImprove && gradingResult.areasToImprove.length > 0 && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Areas to Improve
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-1">
                    {gradingResult.areasToImprove.map((area, idx) => (
                      <li key={idx} className="text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Question Results */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Detailed Results</h3>
              {editableQuiz.questions.map((question, idx) => {
                const result = getQuestionResult(question.id);
                const isCorrect = result?.isCorrect;
                const isExpanded = showExplanation === question.id;
                
                return (
                  <Card 
                    key={question.id} 
                    className={cn(
                      "transition-all cursor-pointer",
                      isCorrect 
                        ? 'border-green-200 dark:border-green-800 hover:border-green-300' 
                        : 'border-red-200 dark:border-red-800 hover:border-red-300'
                    )}
                    onClick={() => setShowExplanation(isExpanded ? null : question.id)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {isCorrect ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm mb-2">
                            {idx + 1}. {question.question}
                          </p>
                          <div className="text-sm space-y-1">
                            <p>
                              <span className="text-muted-foreground">Your answer:</span>{' '}
                              <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                                {getAnswerDisplayText(question, answers[question.id])}
                              </span>
                            </p>
                            {!isCorrect && (
                              <p>
                                <span className="text-muted-foreground">Correct answer:</span>{' '}
                                <span className="text-green-600">{getAnswerDisplayText(question, question.correctAnswer)}</span>
                              </p>
                            )}
                          </div>
                          
                          {/* Expandable explanation */}
                          {isExpanded && (result?.explanation || question.explanation) && (
                            <div className="mt-3 p-3 bg-muted rounded-lg">
                              <div className="flex items-start gap-2">
                                <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5" />
                                <div>
                                  <p className="text-xs font-medium mb-1">Explanation</p>
                                  <p className="text-sm text-muted-foreground">
                                    {result?.explanation || question.explanation}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <ChevronRight className={cn(
                          "w-4 h-4 text-muted-foreground transition-transform",
                          isExpanded && "rotate-90"
                        )} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => handleRetry('all')} className="flex-1 gap-2">
                <RotateCcw className="w-4 h-4" />
                Retake Quiz
              </Button>
              {gradingResult.correctAnswers < gradingResult.totalQuestions && (
                <Button onClick={() => handleRetry('incorrect')} variant="outline" className="flex-1 gap-2">
                  <BookOpen className="w-4 h-4" />
                  Review Incorrect
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" disabled={isExporting}>
                    <Download className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover">
                  <DropdownMenuItem onClick={() => exportToPdf('quiz-results', editableQuiz, gradingResult)}>
                    <FileImage className="w-4 h-4 mr-2" />
                    PDF with Results
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportToDocx('quiz-results', editableQuiz, gradingResult)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Word with Results
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={exportResults}>
                    <Download className="w-4 h-4 mr-2" />
                    JSON Data
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </ScrollArea>
      </TooltipProvider>
    );
  }

  // Quiz View
  return (
    <TooltipProvider>
      <div className={`flex flex-col h-full ${className}`}>
        {/* Progress Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{editableQuiz.title}</h3>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode('edit')}
                className="gap-1"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </Button>
              <Badge variant="outline" className="gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(elapsedSeconds)}
              </Badge>
              <Badge variant="outline">
                {answeredCount}/{totalQuestions}
              </Badge>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => setShowShortcuts(!showShortcuts)}
                  >
                    <Keyboard className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Keyboard shortcuts (?)</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Keyboard Shortcuts */}
        {showShortcuts && (
          <Card className="mb-4 bg-muted/50">
            <CardContent className="py-3 px-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">←/→</kbd> Navigate</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">1-5</kbd> Select option</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">T/F</kbd> True/False</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Question Card */}
        <Card className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">
                  Question {currentIndex + 1} of {totalQuestions}
                </Badge>
                <div className="flex items-center gap-2">
                  {currentQuestion.difficulty && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs capitalize",
                        currentQuestion.difficulty === 'easy' && "text-green-600 border-green-300",
                        currentQuestion.difficulty === 'hard' && "text-red-600 border-red-300"
                      )}
                    >
                      {currentQuestion.difficulty}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs capitalize">
                    {currentQuestion.type.replace('-', ' ')}
                  </Badge>
                </div>
              </div>
              <CardTitle className="text-base mt-3">
                {currentQuestion.question}
              </CardTitle>
              {currentQuestion.scenarioContext && (
                <div className="text-sm text-muted-foreground italic mt-2 p-3 bg-muted rounded-lg border-l-4 border-primary">
                  {currentQuestion.scenarioContext}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(currentQuestion.type === 'multiple-choice' || (currentQuestion.type === 'scenario' && currentQuestion.options)) && currentQuestion.options && (
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
                >
                  {currentQuestion.options.map((option, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-center space-x-3 p-3 rounded-lg border transition-all",
                        answers[currentQuestion.id] === String(idx)
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50 hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem value={String(idx)} id={`option-${idx}`} />
                      <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                        <span className="text-muted-foreground mr-2">{idx + 1}.</span>
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {currentQuestion.type === 'true-false' && (
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion.id, value)}
                >
                  {['True', 'False'].map((option) => (
                    <div
                      key={option}
                      className={cn(
                        "flex items-center space-x-3 p-3 rounded-lg border transition-all",
                        answers[currentQuestion.id] === option.toLowerCase()
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50 hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem value={option.toLowerCase()} id={option} />
                      <Label htmlFor={option} className="flex-1 cursor-pointer font-medium">
                        {option}
                        <span className="ml-2 text-xs text-muted-foreground">
                          (Press {option.charAt(0)})
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {currentQuestion.type === 'short-answer' && (
                <Textarea
                  placeholder="Type your answer here..."
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                  className="min-h-[150px] resize-none"
                />
              )}

              {/* Scenario context hint */}
              {currentQuestion.scenarioContext && currentQuestion.type !== 'scenario' && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-700 dark:text-amber-400">Hint: Consider the scenario context above.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
            </TooltipTrigger>
            <TooltipContent>← Arrow key</TooltipContent>
          </Tooltip>

          {/* Question Dots */}
          <div className="flex gap-1.5 flex-wrap justify-center max-w-[250px]">
            {editableQuiz.questions.map((q, idx) => {
              const isAnswered = !!answers[q.id];
              const isCurrent = idx === currentIndex;
              
              return (
                <Tooltip key={q.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setCurrentIndex(idx)}
                      className={cn(
                        'w-3 h-3 rounded-full transition-all',
                        isCurrent && 'ring-2 ring-primary ring-offset-1',
                        isAnswered 
                          ? 'bg-primary' 
                          : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    Q{idx + 1}{isAnswered ? ' ✓' : ''}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          {currentIndex === totalQuestions - 1 ? (
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isGrading}
              className="min-w-[100px]"
            >
              {isGrading ? 'Grading...' : 'Submit Quiz'}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>→ Arrow key</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default QuizViewer;
